/**
 * MermaidDiagram — renders a ```mermaid fenced block (PLAN-DIAGRAMS.md) as an SVG.
 *
 * Mermaid is LAZY-loaded (dynamic import, one shared init) so it never weighs on surfaces without a
 * diagram. LLM-authored Mermaid can have syntax slips, so a render failure MUST degrade gracefully:
 * we show the diagram SOURCE + a small note, never a blank section or a thrown error (the plan .md
 * already carries the ASCII flow as its own fallback). securityLevel 'strict' → Mermaid sanitizes the
 * SVG it returns.
 *
 * ZOOM PANEL (user ask 2026-09-24): a STANDARD control cluster (`−  100%  +  ⤢`) sits at the diagram's
 * lower-right on EVERY mermaid — no separate "Expand" button. Inline, +/− scale the diagram in place
 * (the container scrolls); ⤢ opens the SAME already-rendered SVG full-screen (portal to document.body
 * so no overflow ancestor clips it) with the same panel plus wheel-zoom and drag-to-pan. One reusable
 * <ZoomPanel/> so the affordance is identical everywhere a diagram renders.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

let _mermaidPromise = null;
function _loadMermaid() {
  if (!_mermaidPromise) {
    _mermaidPromise = import('mermaid')
      .then((m) => {
        const mermaid = m.default || m;
        mermaid.initialize({ startOnLoad: false, securityLevel: 'strict', theme: 'neutral' });
        return mermaid;
      });
  }
  return _mermaidPromise;
}

let _idSeq = 0;

const MIN_SCALE = 0.25;
const MAX_SCALE = 6;
const clampScale = (s) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, s));

// STANDARD ZOOM CONTROL CLUSTER — `−  100%  +  <action>` pinned lower-right. Shared by the inline
// diagram (action = ⤢ maximize) and the full-screen viewer (action = ✕ close) so the affordance reads
// the same everywhere. Purely presentational; the owner holds the scale state.
function ZoomPanel({ scale, onZoomIn, onZoomOut, onReset, isDark, action = null }) {
  const btn = `flex h-6 w-6 items-center justify-center rounded text-sm font-semibold leading-none transition-colors ${
    isDark ? 'text-slate-100 hover:bg-slate-600/70' : 'text-slate-600 hover:bg-slate-200/80'}`;
  return (
    <div
      className={`flex items-center gap-0.5 rounded-md p-0.5 shadow ${
        isDark ? 'bg-slate-700/85 ring-1 ring-slate-500/40' : 'bg-white/90 ring-1 ring-slate-200'}`}
      data-testid="mmd-zoom-panel"
      // clicks on the panel must not bubble to the fullscreen backdrop / diagram
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <button type="button" className={btn} onClick={onZoomOut} title="Zoom out (−)" aria-label="Zoom out">−</button>
      <button
        type="button"
        className={`min-w-[2.75rem] rounded px-1 text-center text-[11px] font-medium tabular-nums ${
          isDark ? 'text-slate-200 hover:bg-slate-600/70' : 'text-slate-500 hover:bg-slate-200/80'}`}
        onClick={onReset}
        title="Reset zoom (0)"
        aria-label="Reset zoom"
        data-testid="mmd-zoom"
      >
        {Math.round(scale * 100)}%
      </button>
      <button type="button" className={btn} onClick={onZoomIn} title="Zoom in (+)" aria-label="Zoom in">+</button>
      {action}
    </div>
  );
}

// FULL-SCREEN VIEWER — portal overlay reusing the rendered SVG with zoom + drag-pan + wheel-zoom and
// the shared ZoomPanel. Esc / ✕ / backdrop close.
function MermaidFullscreen({ svg, isDark, onClose }) {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);            // {startX, startY, ox, oy} while dragging

  const zoomBy = useCallback((f) => setScale((s) => clampScale(s * f)), []);
  const reset = useCallback(() => { setScale(1); setOffset({ x: 0, y: 0 }); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '+' || e.key === '=') zoomBy(1.2);
      else if (e.key === '-' || e.key === '_') zoomBy(1 / 1.2);
      else if (e.key === '0') reset();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, zoomBy, reset]);

  const onWheel = (e) => { e.preventDefault(); zoomBy(e.deltaY < 0 ? 1.1 : 1 / 1.1); };
  const onPointerDown = (e) => {
    dragRef.current = { startX: e.clientX, startY: e.clientY, ox: offset.x, oy: offset.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset({ x: d.ox + (e.clientX - d.startX), y: d.oy + (e.clientY - d.startY) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  const closeBtn = (
    <button
      type="button"
      className={`ml-0.5 flex h-6 w-6 items-center justify-center rounded text-xs transition-colors ${
        isDark ? 'text-slate-100 hover:bg-slate-600/70' : 'text-slate-600 hover:bg-slate-200/80'}`}
      onClick={onClose}
      title="Close (Esc)"
      aria-label="Close full screen"
      data-testid="mmd-fullscreen-close"
    >
      ✕
    </button>
  );

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-black/70 backdrop-blur-sm"
      data-testid="mmd-fullscreen"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="absolute inset-0 cursor-grab overflow-hidden active:cursor-grabbing"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
      >
        <div
          className="absolute left-1/2 top-1/2 origin-center [&_svg]:max-w-none"
          style={{ transform: `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${scale})` }}
          // eslint-disable-next-line react/no-danger — Mermaid strict-mode SVG, already sanitized
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
      <div className="absolute bottom-3 right-3 z-10">
        <ZoomPanel scale={scale} isDark={isDark} action={closeBtn}
                   onZoomIn={() => zoomBy(1.2)} onZoomOut={() => zoomBy(1 / 1.2)} onReset={reset} />
      </div>
    </div>,
    document.body,
  );
}

export default function MermaidDiagram({ code, isDark = false }) {
  const [svg, setSvg] = useState('');
  const [failed, setFailed] = useState(false);
  const [scale, setScale] = useState(1);       // inline zoom (scales in place; container scrolls)
  const [full, setFull] = useState(false);
  const idRef = useRef(`mmd-${(_idSeq += 1)}`);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setSvg('');
    const src = (code || '').trim();
    if (!src) return undefined;
    _loadMermaid()
      .then((mermaid) => mermaid.render(idRef.current, src))
      .then((res) => { if (!cancelled) setSvg((res && res.svg) || ''); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [code]);

  if (failed) {
    return (
      <div className={`my-1.5 rounded border ${isDark ? 'border-amber-700/40 bg-amber-950/20' : 'border-amber-200 bg-amber-50'}`}>
        <div className={`px-2.5 py-0.5 text-[9px] border-b ${isDark ? 'text-amber-400 border-amber-700/30' : 'text-amber-600 border-amber-200'}`}>
          diagram failed to render — showing source
        </div>
        <pre className={`px-2.5 py-1.5 text-[10px] font-mono leading-relaxed whitespace-pre-wrap m-0 ${isDark ? 'text-slate-300' : 'text-slate-800'}`}>
          <code>{code}</code>
        </pre>
      </div>
    );
  }
  if (!svg) {
    return (
      <div className={`my-1.5 px-2.5 py-1.5 text-[10px] italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
        rendering diagram…
      </div>
    );
  }

  const zoomBy = (f) => setScale((s) => clampScale(s * f));
  const maximizeBtn = (
    <button
      type="button"
      className={`ml-0.5 flex h-6 w-6 items-center justify-center rounded text-xs transition-colors ${
        isDark ? 'text-slate-100 hover:bg-slate-600/70' : 'text-slate-600 hover:bg-slate-200/80'}`}
      onClick={() => setFull(true)}
      title="Full screen (zoom & pan)"
      aria-label="Open diagram full screen"
      data-testid="mmd-expand"
    >
      ⤢
    </button>
  );

  return (
    <div className="mermaid-diagram relative my-1.5">
      {/* scroll host; the inner div is FULL-WIDTH so at scale 1 the SVG fits exactly as before —
          zoom only scales the transform (no max-width override, no shrink-wrap). */}
      <div className="overflow-auto">
        <div
          className="flex origin-top justify-center"
          style={{ width: '100%', transform: scale === 1 ? undefined : `scale(${scale})` }}
          // eslint-disable-next-line react/no-danger — Mermaid returns its OWN sanitized SVG (securityLevel strict)
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      </div>
      {/* STANDARD ZOOM PANEL — FLOATS over the diagram, lower-right (never reflows it) */}
      <div className="pointer-events-none absolute bottom-1.5 right-1.5 z-10">
        <div className="pointer-events-auto opacity-60 transition-opacity hover:opacity-100">
          <ZoomPanel scale={scale} isDark={isDark} action={maximizeBtn}
                     onZoomIn={() => zoomBy(1.2)} onZoomOut={() => zoomBy(1 / 1.2)} onReset={() => setScale(1)} />
        </div>
      </div>
      {full ? (
        <MermaidFullscreen svg={svg} isDark={isDark} onClose={() => setFull(false)} />
      ) : null}
    </div>
  );
}
